import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActionIcon, Box, Group, Table, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import IconTrash from "@tabler/icons-react/dist/esm/icons/IconTrash.mjs";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import classes from "./advancedSearchHistoryTable.module.scss";
import useAdvancedSearchHistoryTable from "./useAdvancedSearchHistoryTable.logic";
import ActivateReportModal from "../../../../components/Modals/ActivateReportModal";
import ExportSavedDataButton from "./ExportSavedDataButton";

import { useRole } from "../../../../redux/features/user";
import {
  clearAdvancedSearchHistory,
  useSelectAdvancedSearchHistoryData,
} from "../../../../redux/features/advancedSearchHistory/advancedSearchHistorySlice";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import { DeleteConfirmModal } from "../../../../components/DeleteConfirmationModal";
import { TableSkeletonRules } from "../../../../components/Skeletons/TableSkeletonRules";
import DateSearchFilter from "../../../../components/searchOptions/dateSearchFilter/DateSearchFilter";
import SortIcon from "../../../../components/searchOptions/SortIcon";
import ThrottledTextInput from "../../../../components/ThrottledTextInput";
import { dateFormat } from "../../../../utils/dateFormat";

const AdvancedSearchHistoryTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = useSelectAdvancedSearchHistoryData();

  const role = useRole();
  const deleteRulePermission = role === "GRANT_ADMIN" || role === "ADMIN";

  const [deleteId, setDeleteId] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);

  const {
    take,
    page,
    totalCount,
    sort,
    filters,
    errorAdvancedSearchHistoryFetch,
    isPendingAdvancedSearchHistory,
    refetchAdvancedSearchHistory,
    deleteHandler,
    toggleSort,
    handleFilterChange,
    handlePageChange,
    handleTakeChange,
  } = useAdvancedSearchHistoryTable();

  const COLUMNS = [
    {
      key: "author",
      label: t("advancedSearchHistory.author"),
      sortKey: "AUTHOR",
    },
    {
      key: "ruleName",
      label: t("advancedSearchHistory.reportName"),
      sortKey: "RULE_NAME",
    },
  ];

  useEffect(() => {
    return () => {
      dispatch(clearAdvancedSearchHistory());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (errorAdvancedSearchHistoryFetch) {
    toast.error("Error fetching advanced search history", { toastId: 2 });
  }

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    open();
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    close();
  };

  const onRowClick = (id) => {
    navigate(`/advanced-search-history/rule-dashboard/${id}`);
  };

  const rows = data.map((row) => (
    <Table.Tr onClick={() => onRowClick(row.id)} key={row.id}>
      <Table.Td>
        {row.dateCreated ? dayjs(row.dateCreated).format(dateFormat) : ""}
      </Table.Td>
      <Table.Td>{row?.author?.name}</Table.Td>
      <Tooltip label={row.name} disabled={!row.name}>
        <Table.Td>{row.name}</Table.Td>
      </Tooltip>
      <Table.Td
        className={classes["action-cell"]}
        onClick={(e) => e.stopPropagation()}
      >
        <ActivateReportModal
          id={row.id}
          onSuccess={refetchAdvancedSearchHistory}
        />
      </Table.Td>
      <Table.Td
        className={classes["action-cell"]}
        onClick={(e) => e.stopPropagation()}
      >
        <ExportSavedDataButton
          id={row.id}
          name={row.name}
          hasSavedData={row.hasSavedData}
        />
      </Table.Td>
      {deleteRulePermission && (
        <Table.Td
          className={classes["action-cell"]}
          onClick={(e) => e.stopPropagation()}
        >
          <Group justify="center">
            <ActionIcon
              onClick={() => handleOpenDeleteModal(row.id)}
              c="gray"
              variant="transparent"
            >
              <IconTrash />
            </ActionIcon>
          </Group>
        </Table.Td>
      )}
    </Table.Tr>
  ));

  return (
    <>
      <Box className="table-panel">
        <Table
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
          className={classes["advanced-search-history-table"]}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Group>
                  <Group
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleSort("DATE")}
                    gap={4}
                    wrap="nowrap"
                  >
                    {t("advancedSearchHistory.date")}
                    <SortIcon col={"DATE"} sort={sort} />
                  </Group>
                  <DateSearchFilter
                    placeholder={t("advancedSearchHistory.date")}
                    onChange={(dates) => handleFilterChange("DATE", dates)}
                  />
                </Group>
              </Table.Th>

              {COLUMNS.map((col) => (
                <Table.Th key={col.key}>
                  <Group>
                    <Group
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSort(col.sortKey)}
                      gap={4}
                      wrap="nowrap"
                    >
                      {col.label}
                      <SortIcon col={col.sortKey} sort={sort} />
                    </Group>
                    <ThrottledTextInput
                      size="xs"
                      w={120}
                      delay={600}
                      value={filters[col.key]}
                      onThrottledChange={(val) =>
                        handleFilterChange(col.key, val)
                      }
                    />
                  </Group>
                </Table.Th>
              ))}

              <Table.Th style={{ width: 180 }} />
              <Table.Th style={{ width: 140 }} />
              {deleteRulePermission && <Table.Th style={{ width: 100 }} />}
            </Table.Tr>
          </Table.Thead>

          {data.length === 0 && !isPendingAdvancedSearchHistory ? (
            <Table.Tbody>
              <TableNoResults
                colSpan={COLUMNS.length + 3 + (deleteRulePermission ? 1 : 0)}
              />
            </Table.Tbody>
          ) : (
            <RenderTableWithSkeleton />
          )}
        </Table>

        <DeleteConfirmModal
          onClose={handleCloseDeleteModal}
          opened={opened}
          onDelete={() => deleteHandler(deleteId)}
          label={"Are you sure that you want to delete this rule?"}
        />
      </Box>
      {totalCount > 0 && (
        <AppPagination
          onPageChange={handlePageChange}
          onTakeChange={handleTakeChange}
          page={page}
          take={take}
          total={totalCount}
        />
      )}
    </>
  );

  function RenderTableWithSkeleton() {
    if (isPendingAdvancedSearchHistory) {
      return <TableSkeletonRules length={take} />;
    }

    return <Table.Tbody pos="relative">{rows}</Table.Tbody>;
  }
};

export default AdvancedSearchHistoryTable;
